import * as amplitude from "@amplitude/unified";
import { Identify } from "@amplitude/unified";

/**
 * Centralized Amplitude wrapper.
 *
 * Why a wrapper?
 * - Single init guard — events fired before initAll() resolves are queued, not lost.
 * - Validates user_id length (Amplitude rejects ids < 5 chars with 400 — common foot-gun
 *   for numeric DB ids like "148").
 * - Type-safe event names + properties via EventMap — typos become compile errors.
 * - Auto-attaches environment + app_version to every event.
 * - One place to disable / mock / swap providers later.
 * - Swallows all SDK errors so analytics can never break the app.
 */

// -------------------------------------------------------------------------------------
// Event catalog — add new events here. Property types are enforced at every call site.
//
// Naming convention:
//  - Event names: snake_case, lowercase, past-tense verb (e.g. workspace_opened).
//  - Property names: snake_case.
//  - Top-level user-facing entities use product terminology: a "workspace" is the
//    container; "charts", "reports" and "cards" live INSIDE a workspace.
//    (Legacy code calls workspaces "reports" internally — don't be misled.)
//  - `surface` is a discriminator when the same event fires from multiple screens
//    (e.g. chart_expanded from both Dashboard and ReportDetails).
// -------------------------------------------------------------------------------------
export type EventMap = {
  // — Auth —
  user_signed_up:                    { account_type: string; referral_code?: string; device_id?: string; ip?: string };
  user_signed_in:                    { role?: string; company_id?: number; device_id?: string; ip?: string };
  user_logged_out:                   { role?: string; company_id?: number };
  wa_magic_login_success:            { role?: string; company_id?: number };
  wa_magic_login_failed:             Record<string, never>;
  wa_magic_login_no_token:           { href?: string };

  // — Top-level navigation —
  dashboard_viewed:                  { role?: string; company_id?: number };

  // — Workspace lifecycle (the top-level container) —
  workspace_opened:                  { workspace_name: string; workspace_id: number | string; role?: string; company_id?: number };
  workspace_deleted:                 { workspace_name: string; role?: string; company_id?: number };
  workspace_shared:                  { workspace_name: string; recipients_count: number; role?: string; company_id?: number };

  // — In-workspace interactions —
  workspace_date_range_changed:      { workspace_id?: number | string; period_type?: string; start_date?: string; end_date?: string; is_comparison?: boolean; surface: "dashboard" | "report" };
  workspace_accounts_filter_changed: { workspace_id?: number | string; account_count: number; previous_count?: number };
  chart_expanded:                    { chart_id?: number | string; chart_title?: string; chart_type?: string; workspace_id?: number | string; surface: "dashboard" | "report" };
  // KPI / summary tile click on the dashboard — primary drill-down interaction.
  card_clicked:                      { card_title?: string; card_value?: string | number; report_id?: number | string; surface: "dashboard" | "report" };
  // "Report" inside the product is a pivot/cohort table (PivotDataPiece component).
  // Fires when user opens a report — either by clicking its name in the dashboard
  // pivot list (navigates) or by clicking "View Report"/"View Cohort" inside a workspace (modal).
  report_opened:                     { report_id?: number | string; report_name?: string; report_type?: "table" | "cohort" | "pivot"; workspace_id?: number | string; surface: "dashboard" | "report" };

  // — Alerts —
  // NOTE: `report_id` here refers to whatever the alert is bound to in legacy schema
  // (likely the workspace's internal `reportId`). Rename if domain modeling changes.
  data_alert_created:                { report_id: number | string; chart_title: string; role?: string; company_id?: number };

  // — Integrations / connections —
  integration_connected:             { integration_name: string; success: boolean; accounts_count: number };
  integration_disconnected:          { integration_name?: string };
  connection_attempted:              { integration_name: string };

  // — GST Recon —
  page_viewed: {
    feature: string;
    page_name: string;
    page_url: string;
    referrer_page_name?: string;
  };
  button_clicked: {
    feature: string;
    cta_text: string;
    section: string;
    page_url: string;
  };
  filter_applied: {
    feature: string;
    filter_type: "date_range" | "search" | "time_range" | "gstin_filter" | "sort" | "needs_action" | "internal_transfers";
    context?: string;
    period_label?: string;
    start_date?: string;
    end_date?: string;
    search_query?: string;
    result_count?: number;
    sort_key?: string;
    sort_direction?: string;
    page_url: string;
  };
  row_selected: {
    feature: string;
    context: "invoice_reconciliation" | "resolve_mismatches";
    entity_type: string;
    entity_id: string;
    row_position: number;
    match_status?: string;
    claim_status?: string;
    side?: "books" | "gstr";
    page_url: string;
  };
  status_updated: {
    feature: string;
    entity_type: string;
    entity_id?: string;
    entity_count: number;
    previous_status: string;
    new_status: string;
    source: "table" | "drawer" | "bulk" | "mismatch_tab";
    invoice_value?: number;
    page_url: string;
  };
  progress_updated: {
    feature: string;
    progress_type:
      | "gstin_connected"
      | "file_added"
      | "file_removed"
      | "invoice_note_added"
      | "processing_started"
      | "processing_completed"
      | "tally_connected"
      | "tally_synced"
      | "tally_sync_failed"
      | "tally_tdl_downloaded"
      | "tally_tdl_not_downloaded";
    gstin?: string;
    success?: boolean;
    error_code?: string;
    error_message?: string;
    context?: "new" | "reconnect";
    source_name?: string;
    file_count?: number;
    upload_method?: "drag_drop" | "browse";
    gstin_count?: number;
    entity_id?: string;
    duration_seconds?: number;
    invoice_count?: number;
    match_rate?: number;
    matched_count?: number;
    mismatch_count?: number;
    page_url?: string;
  };
  error_shown: {
    feature: string;
    form_name: string;
    field_name?: string;
    error_code: string;
    error_message: string;
    page_url: string;
  };

  // — Supplier Intelligence Promo —
  promo_shown: {
    feature: string;
    promo_type: "banner" | "tooltip" | "infocard" | "chip";
    promo_id: string;
    surface: string;
    page_url: string;
  };
  promo_dismissed: {
    feature: string;
    promo_type: "banner" | "tooltip" | "infocard";
    promo_id: string;
    surface: string;
    page_url: string;
  };
};

export type EventName = keyof EventMap;

// -------------------------------------------------------------------------------------
// Init
// -------------------------------------------------------------------------------------
let initPromise: Promise<unknown> | null = null;
let enabled = false;

/**
 * Initialize Amplitude. Safe to call once at app bootstrap.
 * If VITE_AMPLITUDE_API_KEY is missing, all subsequent calls become no-ops.
 */
export function initAnalytics(): void {
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  if (!apiKey) {
    console.warn("[analytics] VITE_AMPLITUDE_API_KEY missing — analytics disabled");
    return;
  }
  enabled = true;
  initPromise = amplitude.initAll(apiKey, {
    analytics: {
      remoteConfig: { fetchRemoteConfig: true },
      autocapture: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
        elementInteractions: true,
        frustrationInteractions: true,
        pageUrlEnrichment: true,
        // networkTracking emits an event per XHR/fetch. After login the dashboard
        // fires 30–50 backend calls — that floods Amplitude (429s). Leave off.
        networkTracking: false,
        webVitals: true,
      },
    },
    // sampleRate: 1 records every session. Fine for early dev, expensive in prod.
    sessionReplay: { sampleRate: import.meta.env.PROD ? 0.1 : 0.2 },
    engagement: {},
  });
}

// -------------------------------------------------------------------------------------
// User identity
// -------------------------------------------------------------------------------------

/**
 * Amplitude requires user_id length 5–1024. Numeric ids like "148" get rejected
 * with `400 Invalid id length for user_id or device_id` and ALL subsequent events
 * for that user are dropped. Prefix short ids to satisfy the constraint.
 */
function normalizeUserId(id: number | string): string | null {
  if (id == null || id === "") return null;
  const raw = String(id);
  return raw.length < 5 ? `user_${raw}` : raw;
}

export type IdentifyUserInput = {
  id: number | string | null | undefined;
  name?: string | null;
  phoneNumber?: string | null;
  companyId?: number | string | null;
  companyName?: string | null;
  companyType?: string | null; // "brand" | "agency"
  role?: string | null;
  deviceId?: string | null;
  ip?: string | null;
};

// Amplitude "group" type used to roll individual users up to their company —
// lets Amplitude report on distinct companies (e.g. "how many companies used
// feature X"), not just distinct users, since one company here has many users.
const COMPANY_GROUP_TYPE = "company";

/** Set the current user id + identify props. Call once on login. */
export function identifyUser(user: IdentifyUserInput): void {
  if (!enabled || !user) return;
  const userId = normalizeUserId(user.id);
  if (!userId) {
    console.warn("[analytics] identifyUser called without a valid id — skipping");
    return;
  }
  try {
    amplitude.setUserId(userId);
    const obj = new Identify();
    if (user.name != null)        obj.set("name",         user.name);
    if (user.phoneNumber != null) obj.set("phone_number",  user.phoneNumber);
    if (user.companyId != null)   obj.set("company_id",   user.companyId as any);
    if (user.companyName != null) obj.set("company_name", user.companyName);
    if (user.companyType != null) obj.set("company_type", user.companyType);
    if (user.role != null)        obj.set("role",         user.role);
    if (user.deviceId != null)    obj.set("device_id",    user.deviceId);
    if (user.ip != null)          obj.set("ip",           user.ip);
    amplitude.identify(obj);

    if (user.companyId != null) {
      const groupId = String(user.companyId);
      amplitude.setGroup(COMPANY_GROUP_TYPE, groupId);
      if (user.companyName != null || user.companyType != null) {
        const groupObj = new Identify();
        if (user.companyName != null) groupObj.set("company_name", user.companyName);
        if (user.companyType != null) groupObj.set("company_type", user.companyType);
        amplitude.groupIdentify(COMPANY_GROUP_TYPE, groupId, groupObj);
      }
    }
  } catch (err) {
    console.warn("[analytics] identifyUser failed", err);
  }
}

/** Clear the current user (call on logout). */
export function resetUser(): void {
  if (!enabled) return;
  try {
    amplitude.reset();
  } catch (err) {
    console.warn("[analytics] reset failed", err);
  }
}

// -------------------------------------------------------------------------------------
// Track
// -------------------------------------------------------------------------------------

/**
 * Track a typed event. Waits for init to complete; never throws.
 * Auto-attaches `environment` and `app_version` to every event.
 */
export function track<E extends EventName>(event: E, props: EventMap[E]): void {
  if (!enabled) return;
  const send = () => {
    try {
      amplitude.track(event, {
        ...props,
        environment: import.meta.env.MODE,
        app_version: import.meta.env.VITE_APP_VERSION,
      });
    } catch (err) {
      console.warn("[analytics] track failed", event, err);
    }
  };
  if (initPromise) {
    initPromise.then(send, send);
  } else {
    send();
  }
}
